/** Platform-independent usage rights. */
export interface UsageRights {
  days: number;
}

// ── Campaign types — matches the BE's CampaignDetails response ───────────────
//
// Source of truth: internal/campaigns/domain/campaign.go (Campaign struct)
// + internal/campaigns/dto.go (CampaignDetails wraps *Campaign + enrichment)
//
// All brief fields live under `brief` (nested), NOT at campaign root.
// `creator_preference` and `targeting` are also nested objects.
//
// See ADR-0001 and internal/campaigns/list_response.go for buildCampaignDetails.

export interface CampaignBrief {
  /** Required. The primary creative focus / script direction. */
  primary_focus: string;
  video_title: string;
  video_description: string;
  secondary_focus: string;
  cta: string;
  /** The BE's `dos` json tag has no omitempty, so an unset/nil slice
   *  serializes as `null` rather than being omitted — always guard with
   *  `?? []` before reading `.length`/mapping. */
  dos: string[] | null;
  /** See `dos` — same null-when-unset behavior. */
  donts: string[] | null;
  sample_video_url: string;
  /** true/false from create; absent/null on older campaigns, which are
   *  checked like true. Mirrors Go's RequiresDisclosure *bool. */
  requires_disclosure?: boolean | null;
}

export interface CampaignCreatorPreference {
  creator_count: number;
  categories: string[];
  tiers: string[];
  creator_genders?: string[];
  /** Free-text context alongside creator_genders, e.g. "open to all but lean female". */
  gender_notes?: string;
}

export interface CampaignDemographics {
  age_min: number;
  age_max: number;
  genders?: string[];
  languages?: string[];
  regions?: string[];
}

export interface CampaignTargeting {
  demographics: CampaignDemographics;
}

/**
 * Product — one entry in a campaign's Multi-Product Campaigns catalog.
 * Source of truth: internal/campaigns/domain/campaign.go (domain.Product).
 * Referenced by product_id from a roster row's assigned_products; 0 or 1 product keeps
 * the assignment UI dormant everywhere (activation gate is >= 2 products).
 */
export interface Product {
  product_id: string;
  name: string;
  product_url?: string;
  notes?: string;
}

/**
 * ProductInput — payload for PUT /campaigns/:id/brief. Omit product_id for new
 * entries (server mints one); include it to preserve an existing product so
 * downstream assignments stay valid.
 */
export type ProductInput = {
  product_id?: string;
  name: string;
  product_url?: string;
  notes?: string;
};

/**
 * Body of POST /api/v1/campaigns — the BE's CreateCampaignRequest
 * (internal/campaigns/dto.go). Built by the create wizard.
 */
export interface CreateCampaignRequest {
  name: string;
  description: string;
  platform: string;
  primary_goal: string;
  /** Outreach market, chosen at creation. */
  region: 'IN' | 'US';
  /** null when no budget was typed (gifting_only / revenue_share), distinct from 0. */
  total_budget: number | null;
  /** One of the 8 currencies; absent = INR. `total_budget` is typed in it. */
  budget_currency?: string;
  payout_model: string;
  products: ProductInput[];
  deliverables_text: string;
  deliverables: string[];
  usage_rights: UsageRights | null;
  creator_preference: CampaignCreatorPreference;
  targeting: CampaignTargeting;
  brief: CampaignBrief;
  /** Sent only for an Instagram campaign with a Reel deliverable and verified audio. */
  official_audio?: OfficialAudio;
  /** Admin only: the brand to create under. Brand callers omit it (their own brand). */
  brand_id?: string;
}

/**
 * Campaign — the full per-campaign payload from GET /api/v1/campaigns/{id}.
 *
 * Maps to the BE's CampaignDetails DTO which embeds *domain.Campaign and
 * adds `brand_name` (enriched from brands collection) + `creators_count`.
 *
 * Non-optional fields are guaranteed by the BE for every valid campaign.
 * Fields typed `| null` may arrive as null from Go omitempty semantics.
 */
/**
 * Which optional production stages a campaign runs. Absent on the campaign
 * means every stage is enabled. Script/content include their human review.
 */
export interface CampaignPipeline {
  script_enabled: boolean;
  content_enabled: boolean;
  brand_safety_enabled: boolean;
}

/** Official Audio Compliance — the campaign's required Instagram sound(s).
 *  Instagram only; absent when the campaign has no audio configured. */
export interface OfficialAudio {
  /** Numeric Instagram audio ids (strings — IG ids are 64-bit). */
  audio_ids?: string[];
  /** Canonical instagram.com/reels/audio/<id>/ URLs, aligned 1:1 with ids. */
  audio_urls?: string[];
  /** Resolved song/artist per audio, aligned with audio_ids. Populated at
   *  set-time (or by the backfill); powers the song-name fallback match. */
  tracks?: Array<{ audio_id: string; song_name?: string; artist_name?: string; cover_url?: string }>;
}

/** Result of resolving one Instagram audio URL to its display metadata
 *  (GET-side of the Official Audio Compliance setup). `resolved` is false when
 *  Instagram's page couldn't be read — the id is still usable, the rest empty. */
export interface ResolvedAudio {
  audio_id: string;
  audio_url: string;
  track_name?: string;
  artist_name?: string;
  cover_url?: string;
  resolved: boolean;
}

// Only instagram.com/reels/audio/<id>/ URLs are valid campaign audio — mirrors
// the BE guard (campaigns/domain.ParseOfficialAudioURL). Lives here so every FE
// surface (the create flow and the shared edit form) validates from one source
// instead of a copy of the regex per app.
const INSTAGRAM_AUDIO_URL = /^(?:https?:\/\/)?(?:www\.)?instagram\.com\/reels\/audio\/\d+\/?(?:[?#].*)?$/i;

/** True when `raw` is a valid Instagram audio URL (used to gate Verify before
 *  the resolve round-trip). Trims first; empty/other URLs return false. */
export function isInstagramAudioUrl(raw: string): boolean {
  return INSTAGRAM_AUDIO_URL.test(raw.trim());
}

export interface Campaign {
  id: string;
  /** Campaign slug — may be absent on legacy campaigns. */
  slug?: string;
  /** Short-link campaign code (collision-adjusted). Absent on legacy campaigns
   *  without one — short-link builders fall back to deriving it from `slug`. */
  short_code?: string;
  brand_id: string;
  /** Display name of the owning brand — populated by detail endpoints. */
  brand_name?: string;
  name: string;
  description: string;
  platform: string;
  /** BE-computed on the detail endpoints: the metrics worker is running, a
   *  fetcher exists for `platform`, and it is not reporting a refused API key.
   *  Absent on an older backend — callers then fall back to the platform name. */
  metrics_fetch_available?: boolean;
  primary_goal: string;
  /**
   * Undefined ("key absent") when the brand never typed a budget for a
   * gifting_only/revenue_share campaign — distinct from an explicit 0. The
   * BE's total_budget is a *float64 with `omitempty`, so a nil value is
   * never sent as `null`, the key is simply missing. fixed_fee campaigns
   * always have a real value >= 1.
   */
  total_budget?: number;
  /** Omitted for INR. */
  budget_currency?: string;
  spend_to_date: number;
  payout_model: string;
  /**
   * Active deliverable type IDs for this campaign.
   * e.g. ["youtube_integrated","youtube_shorts"] or ["instagram_stories","instagram_collab_reel"]
   * Set by NormalizeDeliverables at creation — never empty after creation.
   */
  deliverables: string[];
  /** Free-form description of what creators should deliver. Platform-agnostic. */
  deliverables_text: string;
  /** Content usage rights — platform-independent. Absent when not selected. */
  usage_rights?: UsageRights;
  /** The campaign's required Instagram audio (Official Audio Compliance). */
  official_audio?: OfficialAudio | null;
  /** Production stages this campaign runs. Absent = all enabled. */
  pipeline?: CampaignPipeline | null;
  status: string;
  /**
   * Background analytics-fetching state for this campaign — independent of
   * `status` (a campaign can keep running while fetching is paused).
   * `"stopped" | "active"`. Absent on legacy responses predating the
   * fetch-status endpoint; treat as `"active"` when absent.
   */
  fetch_status?: string;
  creator_preference: CampaignCreatorPreference;
  targeting: CampaignTargeting;
  /** All brief / script-direction fields live here — never at campaign root. */
  brief: CampaignBrief;
  /** Multi-Product Campaigns catalog. Absent/empty for single-product campaigns
   *  (BE omitempty). The assignment UI only activates when length >= 2. */
  products?: Product[];
  /** Total roster entry count — enriched by detail endpoints. */
  creators_count: number;
  created_at: string;
  updated_at: string;
  /** Set when admin has emailed the brief to at least one creator. */
  brief_sent_at?: string;
  /** Earliest went_live_at across live roster entries — used for chart window. */
  first_live_at?: string;
  /** Per-campaign integration toggles (Go: Campaign.Integrations, json:"integrations,omitempty"). */
  integrations?: {
    shopify?: { enabled: boolean };
  };
  /** Caller's effective access level on this campaign (read | write | admin).
   *  Populated by GET /campaigns/:id. Absent on list responses. */
  my_access_level?: 'read' | 'write' | 'admin';
  /** True once any roster entry has reached Confirmed (accepted_by_creator) —
   *  the brief is permanently frozen. Computed at request time (BE:
   *  CampaignDetails.BriefLocked), not stored. UI gating only. */
  brief_locked: boolean;
}

export type { Deliverable } from './deliverable';
